// useAccount — the account read surface (contract 07 §9, ratified R10). Exposes the identity
// (`userId`/`userEmail`) + selected-account context (`accountId`/`account`/`accountDetail`) + the
// four R10 live getters that had legacy importers: `accountUserId`/`accountEmail`/`accountLevel`
// (selected-account owner + caller level, from the store) and `isCreativePkg` (via the pure
// `PackageService`, `packageId === 12` — R10 routed it here, not onto the store).
//
// `selectAccount(a)` (ORP-18636) is the switch half: it DELEGATES to `SelectAccountFlow`
// (account-only bootstrap → reset workspace scope → re-pick → re-hydrate → finalize) and returns
// that flow's outcome so the CALLER owns the copy — a flow never toasts and never resolves i18n.
//
// `renameUser(name)` (CM-22-05) and `loadSelectedAccountContext(force)` (ledger B6) are the two
// WRITE-adjacent seams module 22's settings panes and module 11's Stripe return call. Both live
// here rather than on a store because both need the store READ that the v3 shapes stole from the
// wrong layer — `AuthService.updateUserName` was a static that dynamic-imported `accountStore`
// (contract 06 §3 drops it), and `useAccount()` is the sanctioned place to turn store state into
// an argument.
import { computed, type ComputedRef } from 'vue'

import {
  LoadAccountContextFlow,
  type AccountContextReloadOutcome,
} from '../flows/LoadAccountContextFlow'
import { SelectAccountFlow, type AccountSwitchOutcome } from '../flows/SelectAccountFlow'
import type {
  AccountDetail,
  AccountItem,
  UserAccessInfo,
  UserResponse,
} from '../models/account.types'
import { AccountService } from '../service/AccountService'
import { PackageService } from '../service/PackageService'
import { useAccountStore } from '../stores/accountStore'

export interface UseAccount {
  /** Effective permission map for the selected workspace/account context. */
  permissions: ComputedRef<Record<string, unknown>>
  accounts: ComputedRef<AccountItem[]>
  account: ComputedRef<AccountItem | null>
  accountId: ComputedRef<number | undefined>
  /** App Review-only Meta create-page-ad tool access (ORP-20886). */
  canAccessCreateMetaPageAd: ComputedRef<boolean>
  /** Selected account's package id, exposed for package-specific integration setup. */
  packageId: ComputedRef<number | null>
  accountDetail: ComputedRef<AccountDetail | null>
  user: ComputedRef<UserResponse | null>
  /** The authenticated user's own id (identity), not the selected-account owner. */
  userId: ComputedRef<number | undefined>
  userEmail: ComputedRef<string | undefined>
  /** R10: the SELECTED account's owner id (powers a legacy export-job endpoint id). */
  accountUserId: ComputedRef<number | undefined>
  /** R10: the selected account's owner email. */
  accountEmail: ComputedRef<string | undefined>
  /** R10: the caller's access level on the selected account (powers a settings-tab gate). */
  accountLevel: ComputedRef<unknown>
  /** R10: `packageId === 12`, derived via the pure `PackageService` (not a store getter). */
  isCreativePkg: ComputedRef<boolean>
  /**
   * The caller's per-resource access map (`workspaces` / `dashboards` / `accounts`). Exposed
   * because contract 09 §12 has module 09 read dashboard access levels "from module 07 (never a
   * store directly)" — without this, the only route to `userAccessInfo.dashboards` is an
   * `accountStore` import that contract 09 §15 forbids. Read-only, additive (ORP-18657).
   */
  userAccessInfo: ComputedRef<UserAccessInfo>
  /** Switch account (contract §9 → `SelectAccountFlow`). Re-selecting the CURRENT account is a
   *  no-op — never a re-boot (fixes the v3 `selectedAccountid` guard bug, analysis R9). */
  selectAccount: (account: AccountItem | null) => Promise<AccountSwitchOutcome>
  /**
   * Rename the AUTHENTICATED user (CM-22-05) — the replacement for v3's static
   * `AuthService.updateUserName`, which contract 06 §3 drops. Takes only the new name; the user id
   * comes from the store HERE, so no layer below reaches for it.
   *
   * REJECTS with `ApiError` on failure — do not call it bare. The v3 call site was `try/finally`
   * with no `catch` (`ProfileSettings.vue:85-97`), so a failed rename still showed "Username
   * successfully updated."; the caller must catch and render its own copy.
   *
   * Writes NOTHING to the store on success — pair it with `loadSelectedAccountContext(true)` to
   * re-read the renamed user (contract 22 §11).
   */
  renameUser: (name: string) => Promise<void>
  /**
   * Reload the selected account's context (ledger B6 → `LoadAccountContextFlow`): account detail,
   * accounts, access info, rbac and the workspace list, then a permission/package recompute.
   * `force=true` also busts the server cache (`refresh=true`, BE-07-09) and is what module 11's
   * Stripe return and module 22's settings mutations pass; `force=false` is a cold fill that
   * no-ops when context is already warm. Never a re-boot: `session.status` does not move.
   */
  loadSelectedAccountContext: (force?: boolean) => Promise<AccountContextReloadOutcome>
}

export function useAccount(): UseAccount {
  const accountStore = useAccountStore()

  return {
    permissions: computed(() => accountStore.permissions as Record<string, unknown>),
    accounts: computed(() => accountStore.accounts),
    account: computed(() => accountStore.account),
    accountId: computed(() => accountStore.accountId),
    canAccessCreateMetaPageAd: computed(() => accountStore.canAccessCreateMetaPageAd),
    packageId: computed(() => accountStore.packageId),
    accountDetail: computed(() => accountStore.accountDetail),
    user: computed(() => accountStore.user),
    userId: computed(() => accountStore.userId),
    userEmail: computed(() => accountStore.userEmail),
    accountUserId: computed(() => accountStore.accountUserId),
    accountEmail: computed(() => accountStore.accountEmail),
    accountLevel: computed(() => accountStore.accountLevel),
    isCreativePkg: computed(() => PackageService.isCreativePackage(accountStore.packageId)),
    userAccessInfo: computed(() => accountStore.userAccessInfo),
    // Constructed per call, not per `useAccount()`: the flow is a one-shot command object.
    selectAccount: (account) => new SelectAccountFlow().execute(account),
    // The store read v3 hid inside a static, done in the open. `userId` is the AUTHENTICATED
    // user's id (`user.user.id`) — not `accountUserId`, which is the selected account's OWNER.
    renameUser: (name) => new AccountService().renameUser(accountStore.userId, name),
    loadSelectedAccountContext: (force) => new LoadAccountContextFlow().execute(force),
  }
}
